import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, Brain, Home, Lock, Sparkles,
  Eye, Globe, ChevronDown, ChevronRight,
  Check, Star, Package, Heart, Users, AlertCircle
} from 'lucide-react';

const GraffitiScramblerProduct = () => {
  const [selectedTab, setSelectedTab] = useState('overview');
  const [selectedUnit, setSelectedUnit] = useState('base');
  const [quantity, setQuantity] = useState(1);
  const [expandedFaq, setExpandedFaq] = useState(null);

  // Pricing mirrors backend/app/catalog/synthetiq_catalog.py (HARDWARE_CATALOG) —
  // keep these two in sync; that file is the source of truth for Stripe.
  const units = {
    base: {
      name: 'Base Unit',
      price: 799,
      tagline: 'Express secure app-distribution unit with on-device AI model deployment.',
    },
    pro: {
      name: 'Pro Unit',
      price: 1199,
      tagline: 'Extended storage and processing headroom for heavier local workloads.',
    },
  };
  const currency = 'USD';

  const features = [
    {
      icon: Shield,
      title: '23Controls™ Secure Enclave',
      description: 'AES-256 hardware encryption isolates your data and local AI models in a dedicated enclave on your own device — no blockchain, no third-party custodian, just a locked box on your network.',
      color: 'from-purple-500 to-violet-600'
    },
    {
      icon: Brain,
      title: 'Local RAG Processing',
      description: 'All your personal data stays on your device. Medical bills, tax documents, family photos—processed locally with AI that never leaves your home network.',
      color: 'from-fuchsia-500 to-pink-600'
    },
    {
      icon: Sparkles,
      title: 'Entity-Based Interface',
      description: 'Meet your on-screen AI entity — a friendly avatar that surfaces what changed, flags what needs attention, and becomes a familiar face for your Scrambler rather than another dashboard.',
      color: 'from-cyan-500 to-blue-600'
    },
    {
      icon: Home,
      title: 'Smart Home Integration',
      description: 'A network deployment application communicates with your IoT devices from its own secure subnet, orchestrating your smart home ecosystem.',
      color: 'from-emerald-500 to-green-600'
    },
    {
      icon: Lock,
      title: 'Concentric Governance',
      description: 'Four layers of control shape how the device behaves: Physics Laws, Regional Law, Cultural Mores, and Personal Preferences—applied hierarchically.',
      color: 'from-orange-500 to-red-600'
    },
    {
      icon: Heart,
      title: 'Joy-First Design',
      description: "Features like GRIMM's Lore Agent bring your family history to life through AI-powered storytelling and genealogy — tech that's meant to feel warm, not clinical.",
      color: 'from-rose-500 to-pink-600'
    }
  ];

  const includedModels = [
    'Household and medical bills AI review and assistance',
    'ForensIQ™: tax and invoice documents for small business or personal finance (pilot)',
    'Schedule and memo interfaces and controls',
    'Network deploy application for IoT device mapping',
    "Storytelling 'Lore Agent' based on GRIMM models (trial period)",
    'Standard interface features for music and entertainment apps'
  ];

  const technicalSpecs = [
    { category: 'Dimensions', specs: [
      { label: 'Footprint', value: '110mm x 95mm' },
      { label: 'Thickness', value: '35mm' },
      { label: 'Stand', value: 'Fold-out kickstand, 15°-45°' },
      { label: 'Form Factor', value: 'Compact hub, screen-forward' }
    ]},
    { category: 'Display', specs: [
      { label: 'Screen Type', value: 'Color touchscreen (OLED upgrade option)' },
      { label: 'Display Window', value: '95mm x 72mm' },
      { label: 'Interface', value: 'On-screen AI avatar + status bar' },
      { label: 'Orientation', value: 'Portrait, kickstand-propped' }
    ]},
    { category: 'Processing', specs: [
      { label: 'Platform', value: 'Raspberry Pi 5 (BCM2712)' },
      { label: 'Operating System', value: 'Graffiti OS (Ubuntu Server 25 base)' },
      { label: 'Storage', value: 'NVMe-ready, SD card expandable' },
      { label: 'RAM', value: 'Per Raspberry Pi 5 configuration' }
    ]},
    { category: 'Connectivity', specs: [
      { label: 'Network', value: 'Gigabit Ethernet + WiFi' },
      { label: 'Ports', value: '2x USB 3.0, 2x micro-HDMI' },
      { label: 'Power', value: 'USB-C' },
      { label: 'Expansion', value: 'Standard 40-pin GPIO header' }
    ]},
    { category: 'Security', specs: [
      { label: 'Enclave', value: '23Controls™ AES-256 hardware encryption' },
      { label: 'Data Storage', value: 'Local-only RAG processing' },
      { label: 'Network', value: 'Isolated subnet; optional air-gapped mode' },
      { label: 'Access', value: 'SSH / HTTPS / TLS, configurable' }
    ]},
    { category: 'Software', specs: [
      { label: 'Infrastructure', value: 'AWS-backed build and update pipeline' },
      { label: 'Deployment', value: 'Docker containerized' },
      { label: 'Updates', value: 'CI/CD-managed, signed images' },
      { label: 'Extensibility', value: 'Open source, Ubuntu-based' }
    ]},
    { category: 'Manufacturing', specs: [
      { label: 'Designed in', value: 'United States' },
      { label: 'Core Module', value: 'Raspberry Pi (UK design, US distribution)' },
      { label: 'Enclosure Status', value: '3D-printed prototype phase — manufacturing not yet finalized' },
      { label: 'Core Module Status', value: 'Freenove-kit-based, production enclosure in progress' }
    ]},
    { category: 'Compliance & Safety', specs: [
      { label: 'Governance Framework', value: '23Controls-aligned design intent — see our Compliance page for verified status' },
      { label: 'Content Filtering', value: 'Designed to flag hate speech and unsafe content using recognized reference standards' },
      { label: 'Identity Protection', value: 'Local, device-bound profile — no third-party identity ledger' },
      { label: 'User Equality', value: 'Abstracted, non-discriminatory interface design' }
    ]}
  ];

  const faqs = [
    {
      q: 'How does the Graffiti™ Scrambler protect my privacy?',
      a: "All data processing happens locally on your device using RAG (Retrieval-Augmented Generation). Your personal files, medical bills, tax documents, and photos never leave your home network. The device's 23Controls™ secure enclave adds AES-256 hardware encryption on top of that, so data at rest on the unit itself is locked down too."
    },
    {
      q: 'What makes this different from other smart home devices?',
      a: "The Graffiti™ Scrambler keeps processing on-device instead of shipping your data to a cloud model, and layers that with concentric governance (Physics Laws → Regional Law → Cultural Mores → Personal Preferences) instead of a single opaque policy. It also features an entity-based interface that makes AI feel familiar and joyful rather than corporate or cold."
    },
    {
      q: 'Can I extend the functionality with my own code?',
      a: 'Absolutely! The Scrambler runs on Graffiti OS (an Ubuntu Server 25 base) and is fully open source. You can extend functionality from the Ubuntu layer up, or integrate with AWS cloud services for additional features. It supports Docker containers and custom module development.'
    },
    {
      q: 'What is the on-screen entity and how does it work?',
      a: 'Your Scrambler ships with a friendly on-screen avatar that serves as the consistent communication interface — surfacing bill reminders, subscription changes, and device status in plain language instead of a wall of menus. It becomes more useful as it learns your routine.'
    },
    {
      q: 'Is this suitable for families with children?',
      a: 'The Scrambler is designed with content filtering in mind, aiming to flag hate speech, violence, and inappropriate content against recognized reference standards. The abstracted entity interface is built to read as friendly and age-appropriate — if content safety is a hard requirement for your household, verify current behavior before relying on it for unsupervised use.'
    },
    {
      q: "What is GRIMM's Lore Agent?",
      a: "GRIMM is a genealogical research agentic network that helps you trace your family history. The Lore Agent can analyze family photos and documents and put together illustrated stories about your ancestry, presenting history in a narrative format. It's included as a trial feature with the Scrambler."
    }
  ];

  const unit = units[selectedUnit];
  const orderTotal = unit.price * quantity;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Hero Section */}
      <div className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-transparent to-fuchsia-900/20" />
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(147, 51, 234, 0.15) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />

        <div className="container mx-auto px-6 lg:px-12 py-20 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Product Visualization */}
            <div className="relative order-2 lg:order-1">
              <div className="relative group flex items-center justify-center">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/40 to-fuchsia-500/40 blur-3xl group-hover:blur-4xl transition-all duration-700" />

                {/* Device render — the physical enclosure is in 3D-print prototyping;
                    this is a to-scale illustration, not a product photo. */}
                <div className="relative flex flex-col items-center">
                  <div className="relative w-64 h-56 bg-gradient-to-b from-purple-500/70 via-purple-600/70 to-purple-700/70 rounded-[1.75rem] border border-purple-300/30 shadow-2xl shadow-purple-900/50 backdrop-blur-sm overflow-hidden">
                    {/* Screen */}
                    <div className="absolute inset-3 bottom-10 bg-slate-950 rounded-xl border border-purple-400/30 overflow-hidden flex flex-col">
                      <div className="flex items-center justify-between px-2 py-1 text-[9px] text-purple-300">
                        <span>CONNECTED</span>
                        <span className="text-green-400">●</span>
                      </div>
                      <div className="flex-1 flex items-center justify-center">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-b from-slate-200/80 to-slate-400/60" />
                      </div>
                      <div className="px-2 pb-1 text-[8px] text-purple-200/80 leading-tight">
                        AI entity · bill &amp; subscription overview
                      </div>
                    </div>
                    {/* Port row */}
                    <div className="absolute bottom-2 left-3 right-3 flex items-center justify-between gap-1">
                      {['USB-C', 'HDMI', 'HDMI', 'USB', 'USB', 'ETH'].map((p, i) => (
                        <div key={i} className="flex-1 h-2 rounded-sm bg-slate-900/70 border border-purple-300/20" title={p} />
                      ))}
                    </div>
                  </div>
                  {/* Kickstand */}
                  <div className="w-20 h-10 bg-gradient-to-b from-purple-700/60 to-purple-900/60 rounded-b-xl -mt-1 border-x border-b border-purple-300/20" />
                  <span className="mt-4 text-xs uppercase tracking-wider text-purple-300/70">
                    Prototype render — not a product photo
                  </span>
                </div>
              </div>
            </div>

            {/* Product Info */}
            <div className="space-y-8 order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/30 rounded-full backdrop-blur-sm">
                <Package className="w-4 h-4 text-purple-400" />
                <span className="text-sm font-medium text-purple-300 tracking-wider">
                  SECURE ENCLAVE HARDWARE
                </span>
              </div>

              <div>
                <h1 className="text-6xl lg:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-200 via-fuchsia-200 to-purple-200 mb-4">
                  Graffiti™ Scrambler
                </h1>
                <p className="text-2xl text-purple-100 font-light">
                  Your Personal AI Guardian
                </p>
              </div>

              <p className="text-lg text-slate-300 leading-relaxed">
                A local AI home hub built around a 23Controls™ secure enclave: process sensitive documents, automate your home, and explore your family history — all handled on-device, designed and assembled in the USA.
              </p>

              {/* Price */}
              <div className="bg-gradient-to-r from-purple-900/40 to-fuchsia-900/40 border border-purple-500/30 rounded-2xl p-8 backdrop-blur-sm">
                {/* Unit selector */}
                <div className="flex gap-2 mb-6">
                  {Object.entries(units).map(([id, u]) => (
                    <button
                      key={id}
                      onClick={() => setSelectedUnit(id)}
                      className={`flex-1 text-left px-4 py-3 rounded-xl border transition-colors ${
                        selectedUnit === id
                          ? 'bg-white/10 border-purple-400/60'
                          : 'bg-white/5 border-white/10 hover:border-white/20'
                      }`}
                    >
                      <div className="text-sm font-semibold text-white">{u.name}</div>
                      <div className="text-xs text-slate-400">${u.price}</div>
                    </button>
                  ))}
                </div>

                <div className="flex items-baseline gap-4 mb-2">
                  <span className="text-6xl font-black text-white">${unit.price}</span>
                  <span className="text-2xl text-slate-300">{currency}</span>
                </div>
                <p className="text-sm text-purple-300 mb-6">
                  {unit.tagline}
                </p>

                {/* Quantity Selector */}
                <div className="flex items-center gap-4 mb-6">
                  <label className="text-sm font-medium text-slate-300">Quantity:</label>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-colors"
                    >
                      -
                    </button>
                    <span className="w-12 text-center text-xl font-bold text-white">{quantity}</span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-colors"
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Order Button */}
                <Link
                  to="/login"
                  className="w-full group relative px-8 py-5 bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white font-bold text-xl rounded-xl overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-purple-500/50 hover:scale-105 flex items-center justify-center"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    Order Now — ${orderTotal}
                    <ChevronRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-fuchsia-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </Link>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap gap-4 pt-4">
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Check className="w-4 h-4 text-green-400" />
                  <span>Designed in USA</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Check className="w-4 h-4 text-green-400" />
                  <span>Open Source</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Check className="w-4 h-4 text-green-400" />
                  <span>AES-256 Secured</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur-xl border-b border-white/10">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex gap-1 overflow-x-auto scrollbar-hide">
            {['overview', 'features', 'specs', 'faq'].map((tab) => (
              <button
                key={tab}
                onClick={() => setSelectedTab(tab)}
                className={`px-6 py-4 text-sm font-semibold tracking-wider uppercase transition-all whitespace-nowrap ${
                  selectedTab === tab
                    ? 'text-purple-300 border-b-2 border-purple-400'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="container mx-auto px-6 lg:px-12 py-20">

        {/* Overview Tab */}
        {selectedTab === 'overview' && (
          <div className="space-y-20">
            {/* What It Does */}
            <section>
              <h2 className="text-4xl font-black text-white mb-8">
                A Secure Home Hub That Puts <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-fuchsia-400">You</span> First
              </h2>
              <div className="prose prose-invert prose-lg max-w-none">
                <p className="text-slate-300 leading-relaxed">
                  In a world where your data is constantly harvested and sold, the Graffiti™ Scrambler offers something different: an AI device that keeps your information private, processes everything locally, and locks it behind a hardware-encrypted enclave. No data leaves your home unless you explicitly choose to share it.
                </p>
                <p className="text-slate-300 leading-relaxed">
                  Whether you're organizing medical bills, preparing taxes, researching your genealogy, or just chatting with an AI companion, the Scrambler delivers on-device AI capabilities without compromising your privacy.
                </p>
              </div>
            </section>

            {/* Key Benefits Grid */}
            <section>
              <h2 className="text-4xl font-black text-white mb-12 text-center">
                Why Choose Graffiti™ Scrambler?
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {features.map((feature, idx) => {
                  const Icon = feature.icon;
                  return (
                    <div
                      key={idx}
                      className="group relative bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-white/10 rounded-2xl p-8 hover:border-purple-500/50 transition-all duration-300"
                    >
                      <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${feature.color} rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-3">
                        {feature.title}
                      </h3>
                      <p className="text-slate-400 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Included Models */}
            <section>
              <h2 className="text-4xl font-black text-white mb-8">
                What's Included Out of the Box
              </h2>
              <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-white/10 rounded-2xl p-8">
                <p className="text-slate-300 mb-6">
                  Every Graffiti™ Scrambler comes pre-loaded with AI models designed for everyday use:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {includedModels.map((model, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-gradient-to-br from-purple-500 to-fuchsia-500 rounded-full mt-0.5">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-slate-300">{model}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* ForensIQ Case */}
            <section>
              <h2 className="text-4xl font-black text-white mb-8">
                ForensIQ<span className="text-purple-400">™</span>: Household Paperwork, Actually Organized
              </h2>
              <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-white/10 rounded-2xl p-8">
                <p className="text-slate-300 leading-relaxed mb-4">
                  Nobody decides to end up with four different filing systems — a bank statement here, a tax
                  envelope there, a stack of utility bills on the counter. ForensIQ™ is the case we're building on
                  top of the Scrambler to read across all of it as one local record: household accounts, credit,
                  tax documents, and utility bills, normalized by the same on-device unit instead of four separate
                  apps or a Sunday afternoon of sorting.
                </p>
                <p className="text-slate-300 leading-relaxed mb-6">
                  Because it reads across those categories together, a recommendation about a utility bill can
                  reference what the unit already knows from your accounts — the same local record, not a
                  stitched-together guess.
                </p>
                <div className="flex items-start gap-3 bg-purple-900/20 border border-purple-500/20 rounded-xl p-4">
                  <AlertCircle className="w-5 h-5 text-purple-300 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-slate-400">
                    ForensIQ™ is in active development on the same document-analysis and audit-trail backbone as
                    the Scrambler's other document handling — not yet a finished, end-to-end tested product. We'll
                    update this page as it moves from pilot to generally available.
                  </p>
                </div>
              </div>
            </section>

            {/* Design Philosophy */}
            <section>
              <h2 className="text-4xl font-black text-white mb-8">
                Designed for <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-fuchsia-400">Joy</span>
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div className="space-y-6">
                  <p className="text-slate-300 leading-relaxed">
                    The Graffiti™ Scrambler is a compact, screen-forward hub with a fold-out kickstand — small enough for a desk or counter, with the display propped toward you instead of flat on the table. It's built to feel like a friendly companion, not a cold piece of infrastructure.
                  </p>
                  <p className="text-slate-300 leading-relaxed">
                    The enclosure is currently in 3D-printed prototype phase, built around a Raspberry Pi 5 and a touchscreen display behind the 23Controls™ secure enclave. The on-screen entity gives your Scrambler a face to check in with instead of a wall of settings.
                  </p>
                  <p className="text-slate-300 leading-relaxed">
                    This is a device designed to spark joy, foster comfort, and make AI feel like a natural part of your home—not an intrusion.
                  </p>
                </div>
                <div className="space-y-4">
                  <div className="bg-gradient-to-br from-purple-900/30 to-fuchsia-900/30 border border-purple-500/30 rounded-xl p-6">
                    <div className="flex items-start gap-4">
                      <Star className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="text-lg font-bold text-white mb-2">Fold-Out Kickstand</h4>
                        <p className="text-slate-400">Props the screen toward you at 15°-45°, no separate stand needed</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-900/30 to-fuchsia-900/30 border border-purple-500/30 rounded-xl p-6">
                    <div className="flex items-start gap-4">
                      <Eye className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="text-lg font-bold text-white mb-2">Entity Interface</h4>
                        <p className="text-slate-400">An on-screen avatar surfaces status and alerts in plain language</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-900/30 to-fuchsia-900/30 border border-purple-500/30 rounded-xl p-6">
                    <div className="flex items-start gap-4">
                      <Shield className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="text-lg font-bold text-white mb-2">Secure Enclave</h4>
                        <p className="text-slate-400">AES-256 hardware encryption isolates your data and models</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}

        {/* Features Tab */}
        {selectedTab === 'features' && (
          <div className="space-y-16">
            {/* Concentric Governance */}
            <section>
              <h2 className="text-4xl font-black text-white mb-8">
                Concentric Governance Model
              </h2>
              <p className="text-slate-300 mb-8 max-w-3xl">
                The Graffiti™ Scrambler operates under a four-layer governance structure that shapes how it behaves:
              </p>
              <div className="space-y-4">
                {[
                  {
                    level: 'I. Physics Laws (Axioms)',
                    description: 'Must obey physical laws. Cannot act on unproven physics. No overrides.',
                    color: 'from-red-500 to-orange-500'
                  },
                  {
                    level: 'II. Regional Governing Law',
                    description: 'Must obey local legal authority based on device location. Limited exceptions.',
                    color: 'from-orange-500 to-yellow-500'
                  },
                  {
                    level: 'III. Tribal/Cultural Mores',
                    description: 'Understands and conforms to local cultural norms, language use, and social expectations.',
                    color: 'from-yellow-500 to-green-500'
                  },
                  {
                    level: 'IV. Personal Preferences',
                    description: 'Adapts to user-generated feedback and preferences from RAG collections.',
                    color: 'from-green-500 to-blue-500'
                  }
                ].map((layer, idx) => (
                  <div key={idx} className="bg-gradient-to-r from-slate-800/50 to-slate-900/50 border border-white/10 rounded-xl p-6 hover:border-purple-500/30 transition-all">
                    <div className="flex items-start gap-4">
                      <div className={`flex-shrink-0 w-12 h-12 flex items-center justify-center bg-gradient-to-br ${layer.color} rounded-lg text-white font-black text-xl`}>
                        {idx + 1}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2">{layer.level}</h3>
                        <p className="text-slate-400">{layer.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Safety & Equality */}
            <section>
              <h2 className="text-4xl font-black text-white mb-8">
                Universal Safety & Equality
              </h2>
              <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-white/10 rounded-2xl p-8">
                <div className="prose prose-invert prose-lg max-w-none">
                  <p className="text-slate-300 mb-6">
                    The Graffiti™ Scrambler is designed to treat all users as equal. Through abstraction to an entity-based interface, it's built to avoid prejudice or predisposition based on:
                  </p>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                    {['Gender', 'Race', 'Sexual Preference', 'Political Alliance', 'Age', 'Religion'].map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-slate-300">
                        <Users className="w-5 h-5 text-purple-400" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="text-slate-300 mb-6">
                    <strong className="text-white">Content Filtering:</strong> The device is designed to flag profanity, violence planning, racial slurs, and hate speech against recognized reference standards, including the hate-speech database at{' '}
                    <a href="https://github.com/leondz/hatespeechdata" className="text-purple-400 hover:text-purple-300">
                      github.com/leondz/hatespeechdata
                    </a>.
                  </p>
                  <p className="text-slate-300">
                    <strong className="text-white">Identity Protection:</strong> Your profile stays local to your device — there's no third-party identity ledger or blockchain record of who you are.
                  </p>
                </div>
              </div>
            </section>

            {/* GRIMM Feature Highlight */}
            <section>
              <h2 className="text-4xl font-black text-white mb-8">
                Featured: GRIMM's Lore Agent
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div className="space-y-6">
                  <p className="text-slate-300 leading-relaxed">
                    GRIMM (Genealogical Research Intelligent Memory Module) is an agentic network component that turns genealogy research into a guided, narrative experience.
                  </p>
                  <p className="text-slate-300 leading-relaxed">
                    Upload family photos, documents, and records—all processed locally and privately. The Lore Agent can:
                  </p>
                  <ul className="space-y-3">
                    {[
                      'Trace your roots back to the homeland',
                      'Identify relatives and connections in old photographs',
                      'Generate illustrated narratives about your family history',
                      'Surface cultural traditions, mores, and historical context',
                      "Present your family's story as guided, illustrated narratives"
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Sparkles className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm text-purple-300 italic">
                    * GRIMM's Lore Agent is included as a trial feature with your Graffiti™ Scrambler
                  </p>
                </div>
                <div className="bg-gradient-to-br from-purple-900/40 to-fuchsia-900/40 border border-purple-500/30 rounded-2xl p-8">
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Built for Storytelling
                  </h3>
                  <p className="text-slate-300 leading-relaxed">
                    GRIMM's dialog and collection system is built to walk you through a family story the same way a relative would tell it — one document, one photo, one thread at a time, kept on-device throughout.
                  </p>
                </div>
              </div>
            </section>
          </div>
        )}

        {/* Technical Specs Tab */}
        {selectedTab === 'specs' && (
          <div className="space-y-12">
            <div>
              <h2 className="text-4xl font-black text-white mb-4">
                Technical Specifications
              </h2>
              <p className="text-slate-400 mb-12">
                Designed in the United States. The enclosure shown is a 3D-printed prototype — dimensions and materials are subject to change before a production run.
              </p>
            </div>

            <div className="space-y-8">
              {technicalSpecs.map((section, idx) => (
                <div key={idx} className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-white/10 rounded-2xl overflow-hidden">
                  <div className="bg-gradient-to-r from-purple-900/40 to-fuchsia-900/40 px-8 py-4 border-b border-white/10">
                    <h3 className="text-2xl font-bold text-white">{section.category}</h3>
                  </div>
                  <div className="p-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {section.specs.map((spec, specIdx) => (
                        <div key={specIdx} className="flex justify-between items-start gap-4 pb-4 border-b border-white/5 last:border-b-0">
                          <span className="text-slate-400 font-medium">{spec.label}</span>
                          <span className="text-white text-right">{spec.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Infrastructure Note */}
            <div className="bg-gradient-to-br from-blue-900/20 to-cyan-900/20 border border-blue-500/30 rounded-2xl p-8">
              <div className="flex items-start gap-4">
                <Globe className="w-8 h-8 text-blue-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-white mb-3">Cloud Infrastructure</h3>
                  <p className="text-slate-300 leading-relaxed">
                    The underlying infrastructure for Graffiti™ products is hosted on Amazon Web Services (AWS), used for build, update, and optional cloud features. Core AI processing stays local to your device — cloud features like extended model libraries, remote access, and backup are opt-in.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* FAQ Tab */}
        {selectedTab === 'faq' && (
          <div className="space-y-8">
            <div>
              <h2 className="text-4xl font-black text-white mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-slate-400 mb-12">
                Everything you need to know about the Graffiti™ Scrambler
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div
                  key={idx}
                  className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-white/10 rounded-xl overflow-hidden transition-all duration-300 hover:border-purple-500/30"
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between gap-4 p-6 text-left"
                  >
                    <span className="text-lg font-semibold text-white pr-4">
                      {faq.q}
                    </span>
                    <ChevronDown
                      className={`w-6 h-6 text-purple-400 flex-shrink-0 transition-transform duration-300 ${
                        expandedFaq === idx ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {expandedFaq === idx && (
                    <div className="px-6 pb-6">
                      <p className="text-slate-300 leading-relaxed">
                        {faq.a}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Contact CTA */}
            <div className="mt-16 bg-gradient-to-r from-purple-900/40 to-fuchsia-900/40 border border-purple-500/30 rounded-2xl p-8 text-center">
              <AlertCircle className="w-12 h-12 text-purple-400 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-3">
                Still Have Questions?
              </h3>
              <p className="text-slate-300 mb-6">
                We're here to help you understand how the Graffiti™ Scrambler can transform your relationship with AI.
              </p>
              <button className="px-8 py-3 bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white font-semibold rounded-lg hover:shadow-xl hover:shadow-purple-500/30 transition-all duration-300">
                Contact Support
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Sticky Footer CTA */}
      <div className="sticky bottom-0 z-30 bg-slate-900/95 backdrop-blur-xl border-t border-white/10 py-4">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              <div>
                <div className="text-3xl font-black text-white">${orderTotal} {currency}</div>
                <div className="text-sm text-slate-400">{unit.name} · one-time purchase</div>
              </div>
            </div>
            <Link
              to="/login"
              className="px-8 py-4 bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white font-bold text-lg rounded-xl hover:shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 hover:scale-105"
            >
              Order Now
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GraffitiScramblerProduct;
